// The "other-user" profile popover. Read-only counterpart to the
// `ProfilePopover` used for the local user. Mounted as a Radix
// Popover anchored to the avatar that opens it (member-list row,
// message author, DM header, etc.).
//
// Light grape + white surface to live inside the strawberry/cloud
// world: white card, soft grape border + section tints, strawberry
// banner band, and a punched-out avatar overlapping it. Below the
// identity row: optional role badges (when shown inside a Space),
// a "Mutual Spaces" hint line when applicable, and a DM composer.

import { MessageSquare, Smile } from "lucide-react";
import { useEffect, useState } from "react";

import {
  AvatarFallback,
  AvatarImage,
  Avatar as AvatarRoot,
  Input,
  PopoverContent,
  cn,
} from "@link/ui";

import { StatusDot, STATUS_LABEL } from "./StatusDot";
import { ProfileUsername } from "./ProfileUsername";
import type { BadgeId } from "../lib/badges";
import type { FriendStatus } from "../types/ui";
import { profileBannerStyle } from "../lib/profile-banner";
import { useSelf } from "../state/SelfSession";
import { UserActionsMenu } from "./UserActionsMenu";
import { UserProfileModal } from "./UserProfileModal";
import { FriendActionButton } from "./FriendActionButton";
import { requestDirectMessage } from "../lib/dm-navigation";
import { SelfProfileCardContent } from "./ProfilePopover";
import type { CommunityTagSummary } from "../lib/api";
import { EmojiPicker } from "./messages/EmojiPicker";

export interface MemberRoleBadge {
  /** Stable id (used as the React key). */
  id: string;
  label: string;
  /** Tailwind class for the dot color. Defaults to strawberry-500. */
  dotClass?: string;
  /** Exact server-configured role color. */
  color?: string;
}

export interface MemberProfile {
  name: string;
  tag: string;
  username?: string;
  pronouns?: string | null;
  avatarUrl?: string | null;
  bannerColor?: string;
  bannerMode?: "solid" | "gradient";
  bannerSecondaryColor?: string;
  status: FriendStatus;
  bio: string | null;
  roles: MemberRoleBadge[];
  mutualSpaces: string[];
  badges?: ReadonlyArray<BadgeId>;
  memberSince?: string;
  currentSpaceXid?: string;
  communityTag?: CommunityTagSummary | null;
}

interface Props {
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  member: MemberProfile;
}

export function MemberProfilePopover({
  side = "right",
  align = "start",
  member,
}: Props) {
  const [draft, setDraft] = useState("");
  const [fullProfileOpen, setFullProfileOpen] = useState(false);
  const [compactViewport, setCompactViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = (): void => setCompactViewport(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  // A side-mounted card cannot fit beside a full-width mobile member drawer.
  const placement = compactViewport && (side === "left" || side === "right") ? "bottom" : side;
  const self = useSelf();
  const isSelf = member.tag === self.identity.accountXid;

  if (isSelf) {
    return (
      <PopoverContent
        side={placement}
        align={align}
        sideOffset={12}
        collisionPadding={12}
        sticky="always"
        className="!z-[160] max-h-[calc(100dvh-1.5rem)] w-[min(320px,calc(100vw-1.5rem))] overflow-x-hidden overflow-y-auto rounded-2xl border-cloud-300 bg-white p-0 text-cloud-700 shadow-2xl shadow-black/25"
      >
        <SelfProfileCardContent
          onOpenFullProfile={() => self.setProfileOpen(true)}
        />
      </PopoverContent>
    );
  }

  return (
    <>
    <PopoverContent
      side={placement}
      align={align}
      sideOffset={12}
      collisionPadding={12}
      sticky="always"
      className="!z-[160] max-h-[calc(100dvh-1.5rem)] w-[min(360px,calc(100vw-1.5rem))] overflow-x-hidden overflow-y-auto overscroll-contain rounded-2xl border-cloud-300 bg-white p-0 text-cloud-700 shadow-2xl shadow-black/20"
    >
      {/* Banner — strawberry/peach gradient with depth blobs. */}
      <div className="relative h-24 overflow-hidden rounded-t-2xl">
        <div className="absolute inset-0" style={profileBannerStyle(member.bannerColor ?? "#ee7f9f", member.bannerMode ?? "solid", member.bannerSecondaryColor ?? "#9b7bd3")} />

        {/* Top-right action buttons matching the reference image. */}
        <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
          {!isSelf ? (
            <FriendActionButton accountXid={member.tag} displayName={member.name} compact />
          ) : null}
          <UserActionsMenu accountXid={member.tag} displayName={member.name} excludeSpaceXid={member.currentSpaceXid} />
        </div>
      </div>

      {/* Avatar overlapping the banner */}
      <div className="-mt-12 flex items-end gap-3 px-5">
        <button
          type="button"
          onClick={() => setFullProfileOpen(true)}
          className="group relative rounded-full text-left ring-[6px] ring-cloud-50 transition-transform hover:scale-[1.03]"
          aria-label={`Open full profile for ${member.name}`}
        >
          <AvatarRoot className="h-[84px] w-[84px]">
            {member.avatarUrl != null ? (
              <AvatarImage src={member.avatarUrl} alt="" />
            ) : null}
            <AvatarFallback className="text-2xl font-semibold uppercase">
              {member.name.slice(0, 1)}
            </AvatarFallback>
          </AvatarRoot>
          <span className="absolute bottom-1 right-1 rounded-full bg-cloud-50 p-[3px]">
            <StatusDot
              status={member.status}
              sizeClass="h-3.5 w-3.5"
              ringClass="ring-cloud-50"
            />
          </span>
        </button>
      </div>

      {/* Identity */}
      <div className="px-5 pb-1 pt-3">
          <p className="text-lg font-bold text-cloud-800 [overflow-wrap:anywhere]">
            {member.name}
          </p>
        <ProfileUsername username={member.username ?? member.name} badges={member.badges ?? []}>
          {member.communityTag != null ? <CommunityTagChip tag={member.communityTag} /> : null}
          {member.pronouns != null && member.pronouns.trim().length > 0 ? (
            <span className="rounded-full bg-cloud-100 px-2 py-0.5 text-[10px] font-semibold text-cloud-600 ring-1 ring-cloud-200">{member.pronouns}</span>
          ) : null}
        </ProfileUsername>
        <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-cloud-500">
          <StatusDot
            status={member.status}
            sizeClass="h-2 w-2"
            ringClass="ring-cloud-50"
          />
          {STATUS_LABEL[member.status]}
        </p>
      </div>

      {member.bio !== null ? (
        <div className="mx-5 mt-3 rounded-xl border border-cloud-200 bg-cloud-50 px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-cloud-400">About me</p>
          <p className="mt-1 text-xs leading-relaxed text-cloud-600">{member.bio}</p>
        </div>
      ) : null}

      {/* Roles */}
      {member.currentSpaceXid !== undefined && member.roles.length > 0 ? (
        <div className="px-5 pt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-cloud-400">
            Roles
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {member.roles.map((role) => (
              <span
                key={role.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-grape-50 px-2.5 py-1 text-[11px] font-medium text-cloud-700 ring-1 ring-grape-200"
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    role.color === undefined ? (role.dotClass ?? "bg-strawberry-500") : undefined,
                  )}
                  style={role.color === undefined ? undefined : { backgroundColor: role.color }}
                />
                {role.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Mutual spaces */}
      {member.mutualSpaces.length > 0 ? (
        <div className="px-5 pt-3 text-[11px] text-cloud-500">
          <span className="font-medium text-cloud-700">
            {member.mutualSpaces.length} mutual space
            {member.mutualSpaces.length === 1 ? "" : "s"}
          </span>
          <span className="ml-1 truncate text-cloud-400">
            · {member.mutualSpaces.join(", ")}
          </span>
        </div>
      ) : null}

      {/* Composer */}
      {!isSelf ? <div className="m-4 mt-4 rounded-lg border border-grape-200 bg-grape-50/60 px-3 py-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const message = draft.trim();
            if (message.length === 0) return;
            requestDirectMessage(member.tag, message);
            setDraft("");
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Message @${member.username ?? member.name}`}
            className="h-8 flex-1 border-none bg-transparent text-sm text-cloud-700 placeholder:text-cloud-400 focus-visible:ring-0"
          />
          <EmojiPicker onSelect={(emoji) => setDraft((current) => `${current}${emoji}`)}>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-400 hover:bg-grape-100 hover:text-strawberry-600"
              aria-label="Emoji"
            >
              <Smile className="h-4 w-4" />
            </button>
          </EmojiPicker>
          <button
            type="submit"
            disabled={draft.trim().length === 0}
            className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-400 hover:bg-grape-100 hover:text-strawberry-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-cloud-400"
            aria-label="Send"
          >
            <MessageSquare className="h-4 w-4" />
          </button>
        </form>
      </div> : null}
    </PopoverContent>
    <UserProfileModal
      open={fullProfileOpen}
      member={member}
      onOpenChange={setFullProfileOpen}
    />
    </>
  );
}

export function CommunityTagChip({ tag }: { tag: CommunityTagSummary }) {
  return (
    <span title={`${tag.label} · ${tag.space_name}`} className="inline-flex max-w-40 items-center gap-1 rounded-md bg-grape-100 px-1.5 py-0.5 text-[10px] font-bold text-grape-700 ring-1 ring-grape-200">
      {tag.icon_url != null ? <img src={tag.icon_url} alt="" className="h-3 w-3 rounded object-cover" /> : null}
      <span className="truncate">{tag.code}</span>
    </span>
  );
}
